import { Box, Stack } from '@chakra-ui/react';
import { Illustrations, InteractiveVariants, Notification } from '@pluxee-design-system/core';
import { px, py } from 'common/layouts/layout.styles';
import MainContainer from 'common/layouts/MainContainer';
import { HtmlText, TextTitle } from 'common/typography';
import CampaignLogo from 'components/Affiliation/CampaignLogo';
import OfferCard from 'components/Landing/OfferCard';
import useConfig from 'data/useConfig';
import useContractSwitcher from 'data/useContractSwitcher';
import useCurrentCampaign from 'data/useCurrentCampaign';
import { useCurrentContractId } from 'data/useCurrentContract';
import useSignIn from 'data/useSignIn';
import { I18nProps, withNamespaces } from 'i18n';
import { useCallback, useMemo } from 'react';
import gtm from 'trackers/gtm';
import { AffiliationStatusEnum } from 'types/common';
import routerPush, { getFullBasePath } from 'utils/router';

interface LandingProps extends I18nProps {
  contractId?: string;
  hasMultiple: boolean;
  hasMultipleOnboarding: boolean;
  status: AffiliationStatusEnum;
  onDiscardAffiliation: () => void;
  onDiscardOnboarding: () => void;
}

interface NotificationType {
  description: string;
  onClick: () => void;
  onDiscard?: () => void;
}

const affiliateHandler = () => {
  gtm.trackClickRegisterCompany();
  routerPush('/affiliation');
};

const Landing = ({
  contractId,
  hasMultiple,
  hasMultipleOnboarding,
  onDiscardAffiliation,
  onDiscardOnboarding,
  status,
  t,
  tHtml,
}: LandingProps) => {
  const signInHandler = useSignIn();
  const { setValue: setContractId } = useCurrentContractId();
  const { isoCode, hasAffiliation, hasSelfcare } = useConfig();
  const { campaign } = useCurrentCampaign(isoCode === 'be'); // omit condition isoCode === 'be' when cz ready

  // Permissions
  const isManageDisabled =
    !hasMultiple &&
    !(
      status === AffiliationStatusEnum.NotLogged ||
      status === AffiliationStatusEnum.Onboarding ||
      status === AffiliationStatusEnum.OnboardingDraft ||
      status === AffiliationStatusEnum.Affiliated
    );
  const isNotLoggedIn = status === AffiliationStatusEnum.NotLogged;
  const isOnboardingVisible =
    isoCode === 'be' &&
    (status === AffiliationStatusEnum.Onboarding ||
      status === AffiliationStatusEnum.OnboardingDraft ||
      status === AffiliationStatusEnum.Affiliated);

  // Handler callbacks
  const singleOnboardingHandler = useCallback(
    async (selectedContractId?: string) => {
      if (!selectedContractId) await setContractId(contractId);
      gtm.trackClickRegisterLocation();
      routerPush('/onboarding');
    },
    [contractId, setContractId],
  );
  const singleContractHandler = useCallback(() => {
    gtm.trackClickManageCompany();
    routerPush(isoCode === 'bg' ? '/invoices' : '/dashboard');
  }, [isoCode]);
  const { handleClick: selectOnboardingHandler } = useContractSwitcher(
    t,
    singleOnboardingHandler,
    isOnboardingVisible,
    contractId,
  );
  const { handleClick: selectContractHandler } = useContractSwitcher(
    t,
    singleContractHandler,
    status !== AffiliationStatusEnum.NotLogged,
  );
  const onContractHandler = useCallback(
    () => (hasMultiple ? selectContractHandler(false) : singleContractHandler()),
    [hasMultiple, selectContractHandler, singleContractHandler],
  );
  const onOnboardingHandler = useCallback(
    () => (hasMultiple ? selectOnboardingHandler(false) : singleOnboardingHandler()),
    [hasMultiple, selectOnboardingHandler, singleOnboardingHandler],
  );
  const onOnlyOnboardingHandler = useCallback(
    () => (hasMultipleOnboarding ? selectOnboardingHandler(true) : singleOnboardingHandler()),
    [hasMultipleOnboarding, selectOnboardingHandler, singleOnboardingHandler],
  );
  const createAccountHandler = useCallback(() => {
    gtm.trackClickRegisterCompany();
    signInHandler({ callbackUrl: `${getFullBasePath()}/affiliation`, createAccount: true });
  }, [signInHandler]);

  // Memo
  const notification = useMemo<NotificationType | undefined>(() => {
    switch (status) {
      case AffiliationStatusEnum.AffiliationDraft:
        return {
          description: t(
            'landing_page.status.affiliationDraft',
            'You have not finished your previous registration. Do you want to continue?',
          ),
          onClick: affiliateHandler,
          onDiscard: onDiscardAffiliation,
        };
      case AffiliationStatusEnum.Onboarding:
        return {
          description: t(
            'landing_page.status.onboarding',
            'You have no location assigned to your contract. Do you want to add your first location?',
          ),
          onClick: onOnlyOnboardingHandler,
        };
      case AffiliationStatusEnum.OnboardingDraft:
        return {
          description: t(
            'landing_page.status.onboardingDraft',
            'You have unfinished a location registration. Do you want to continue?',
          ),
          onClick: onOnlyOnboardingHandler,
          onDiscard: onDiscardOnboarding,
        };
      default:
        return undefined;
    }
  }, [status, t, onDiscardAffiliation, onOnlyOnboardingHandler, onDiscardOnboarding]);

  return (
    <MainContainer align="stretch" direction="column" alignItems="center" py={py} px={px}>
      {notification && (
        <Box mt={{ base: 0, md: -6 }} mb="12" width="100%">
          <Notification
            description={notification.description}
            isClosable={Boolean(notification.onDiscard)}
            buttonProps={{
              id: 'Landing_continueButton',
              onClick: notification.onClick,
              children: t('landing_page.status.continue', 'Continue'),
            }}
            onClose={notification.onDiscard}
            variant={InteractiveVariants.INTERACTIVE_WARNING}
          />
        </Box>
      )}
      {campaign && <CampaignLogo campaign={campaign} />}
      <TextTitle
        mb={16}
        title={t('landing_page.pageHeader', 'Welcome to merchant portal')}
        subtitle={tHtml(
          'landing_page.subheader',
          'Manage your company or register new one in simple steps',
        )}
      />
      <Stack spacing="8" align="stretch" direction={{ base: 'column', md: 'row' }}>
        {hasSelfcare && (
          <OfferCard
            buttonText={t('landing_page.portal.button', 'Proceed to Portal')}
            descriptionText={tHtml(
              'landing_page.portal.description',
              'Is your company already registered? Manage your company on the portal.',
            )}
            headerText={t('landing_page.portal.header', 'Manage company')}
            id="Landing_portalProceedButton"
            illustrationBg="core.boldyBlue.5" // TODO: plx/semantic/color/illustration/background8
            illustrationId={Illustrations.OFFICE}
            isDisabled={isManageDisabled}
            onClick={onContractHandler}
          />
        )}

        {hasAffiliation && (
          <OfferCard
            buttonText={t('landing_page.affiliation.button', 'Proceed to Registration')}
            descriptionText={tHtml(
              'landing_page.affiliation.description',
              'Start accepting Pluxee products by registering your company. It is super easy!',
            )}
            headerText={t('landing_page.affiliation.header', 'Register company')}
            id="Landing_registrationProceedButton"
            illustrationBg="core.veryYellow.3" // TODO: plx/semantic/color/illustration/background9
            illustrationId={Illustrations.OFFICE}
            onClick={isNotLoggedIn ? createAccountHandler : affiliateHandler}
          />
        )}

        {isOnboardingVisible && (
          <OfferCard
            buttonText={t('landing_page.onboarding.button', 'Add new location')}
            descriptionText={tHtml(
              'landing_page.onboarding.description',
              'You can simply add a location to already registered company.',
            )}
            headerText={t('landing_page.onboarding.header', 'Register location')}
            id="Landing_onboardingProceedButton"
            illustrationBg="core.darkGreen.4"
            illustrationId={Illustrations.LOCAL_SHOPS}
            onClick={onOnboardingHandler}
          />
        )}
      </Stack>
      {isoCode === 'be' && t('landing_page.legal_disclaimer', '') && (
        <HtmlText as="div" mt={12}>
          {tHtml('landing_page.legal_disclaimer')}
        </HtmlText>
      )}
    </MainContainer>
  );
};

export default withNamespaces(Landing, 'landing_page');
